import { getJson, sendJson } from '@/lib/api';
import type { TrendMarker } from '@bcecloud/portal-ui';

/**
 * The global key-events timeline — typed fetchers over /api/v3/analytics/events.
 * Each event renders as a vertical marker on the analytics trend charts, so a
 * dip or a jump can be read against what changed (e.g. the Aug 28 site move).
 */

/** One key event, as GET /analytics/events returns it. */
export interface AnalyticsEvent {
  readonly id: string;
  readonly label: string;
  /** ISO instant the event occurred (anchored to UTC midnight of the picked day). */
  readonly occursAt: string;
  readonly note: string;
  /** Marker color override (hex), or '' for the chart default. */
  readonly color: string;
  readonly createdByUserId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Key events as chart markers: the event day (YYYY-MM-DD) placed on the time axis. */
export function eventsToMarkers(events: readonly AnalyticsEvent[]): TrendMarker[] {
  return events.map((event) => ({
    x: event.occursAt.slice(0, 10),
    label: event.label,
    ...(event.color ? { color: event.color } : {}),
  }));
}

/** The live key events, optionally bounded to a [from, to] day range (inclusive). */
export function listAnalyticsEvents(
  params?: { from?: string; to?: string },
  signal?: AbortSignal,
): Promise<AnalyticsEvent[]> {
  return getJson<AnalyticsEvent[]>('/analytics/events', params, signal);
}

/** Logs a key event. `occursAt` is a YYYY-MM-DD day. */
export function createAnalyticsEvent(body: {
  label: string;
  occursAt: string;
  note?: string;
  color?: string;
}): Promise<AnalyticsEvent> {
  return sendJson<AnalyticsEvent>('post', '/analytics/events', body);
}

/** Edits a key event. */
export function updateAnalyticsEvent(
  id: string,
  body: { label?: string; occursAt?: string; note?: string; color?: string },
): Promise<AnalyticsEvent> {
  return sendJson<AnalyticsEvent>('patch', `/analytics/events/${id}`, body);
}

/** Removes a key event. */
export function deleteAnalyticsEvent(id: string): Promise<{ id: string }> {
  return sendJson<{ id: string }>('delete', `/analytics/events/${id}`);
}
